import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const features = [
  {
    icon: 'ri-film-line',
    title: 'Premium Quality Films',
    description:
      'We exclusively use top-tier films from 3M, LLumar, and XPEL — brands that deliver measurable heat rejection, UV blocking, and longevity.',
    stat: '77%',
    statLabel: 'Solar Energy Rejected',
    accent: 'from-[#FF6B35] to-[#C9A96E]',
    glow: 'rgba(255,107,53,0.15)',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Certified Technicians',
    description:
      'Every installer on our team holds professional certifications. 15+ years of hands-on precision — zero shortcuts, zero bubbles.',
    stat: '15+',
    statLabel: 'Years Combined Experience',
    accent: 'from-[#C9A96E] to-[#FF6B35]',
    glow: 'rgba(201,169,110,0.15)',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Lifetime Warranty',
    description:
      'Every installation is backed by a comprehensive warranty covering peeling, bubbling, and discoloration — for the life of your vehicle or property.',
    stat: '100%',
    statLabel: 'Satisfaction Guaranteed',
    accent: 'from-[#FF6B35] to-[#C9A96E]',
    glow: 'rgba(255,107,53,0.15)',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Fast Turnaround',
    description:
      'Most automotive tinting jobs are completed the same day. Residential and commercial projects are scheduled to minimize disruption.',
    stat: '1 Day',
    statLabel: 'Typical Auto Completion',
    accent: 'from-[#C9A96E] to-[#FF6B35]',
    glow: 'rgba(201,169,110,0.15)',
  },
];

export function WhyChooseUs() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-32 bg-[#080808] relative overflow-hidden">
      {/* Radial glow backdrop */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,107,53,0.06) 0%, transparent 70%)' }}
      />
      {/* Fine dot texture */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'radial-gradient(circle, #C9A96E 1px, transparent 1px)', backgroundSize: '28px 28px' }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={ref}>
        {/* Header */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''} text-center mb-20`}>
          <span className="luxury-divider mx-auto" />
          <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">
            Why Mr. Tint
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            The Standard Others
            <br />
            <span className="shimmer-gold">Are Measured Against</span>
          </h2>
          <p className="text-white/30 text-sm mt-5 max-w-xl mx-auto leading-relaxed">
            Fifteen years of precision craftsmanship, premium materials, and an unwavering commitment to the perfect finish — every single time.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <div
              key={i}
              className={`anim-hidden ${isVisible ? `animate-fade-in-up delay-${(i + 1) * 100}` : ''} group relative rounded-3xl overflow-hidden cursor-default`}
              style={{ background: '#101010' }}
            >
              {/* Gradient border via pseudo-element technique */}
              <div className="absolute inset-0 rounded-3xl pointer-events-none"
                style={{ padding: '1px', background: 'transparent' }}
              />
              {/* Top accent bar */}
              <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${f.accent} opacity-40 group-hover:opacity-100 transition-opacity duration-500`} />

              {/* Hover glow fill */}
              <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `radial-gradient(ellipse at top left, ${f.glow} 0%, transparent 60%)` }}
              />

              <div className="relative z-10 p-8 h-full flex flex-col border border-white/5 group-hover:border-[#C9A96E]/20 rounded-3xl transition-colors duration-500">
                {/* Icon */}
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-7 bg-gradient-to-br ${f.accent} bg-opacity-10 transition-all duration-300`}
                  style={{ background: `linear-gradient(135deg, rgba(255,107,53,0.12), rgba(201,169,110,0.08))` }}
                >
                  <i className={`${f.icon} text-2xl text-[#FF6B35] group-hover:text-[#C9A96E] transition-colors duration-300`} />
                </div>

                {/* Title */}
                <h3
                  className="text-base font-bold text-white mb-3 tracking-tight leading-snug"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  {f.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-white/40 leading-relaxed flex-1">{f.description}</p>

                {/* Divider */}
                <div className={`my-6 h-px bg-gradient-to-r ${f.accent} opacity-20 group-hover:opacity-50 transition-opacity duration-500`} />

                {/* Stat */}
                <div className="flex items-end justify-between">
                  <div>
                    <div
                      className="text-4xl font-black tracking-tighter shimmer-gold leading-none"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {f.stat}
                    </div>
                    <div className="text-xs text-white/30 mt-1.5 leading-tight">{f.statLabel}</div>
                  </div>
                  <div className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 group-hover:border-[#FF6B35]/40 group-hover:bg-[#FF6B35]/10 transition-all duration-300">
                    <i className="ri-arrow-right-up-line text-white/30 group-hover:text-[#FF6B35] text-sm transition-colors duration-300" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Brand strip */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up delay-500' : ''} mt-16 pt-12 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6`}>
          <p className="text-xs text-white/25 uppercase tracking-widest">Authorised dealer of</p>
          <img
            src="https://mrtintofamerica.com/wp-content/uploads/2024/11/AffiliatesFooter.webp"
            alt="3M, LLumar, XPEL Premium Film Partners"
            className="h-8 opacity-30 grayscale"
          />
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-[#C9A96E] text-sm" />
              ))}
            </div>
            <span className="text-xs text-white/30">500+ five-star reviews</span>
          </div>
        </div>
      </div>
    </section>
  );
}
